/**
 * IfaMeetingPage — Pre-meeting brief for IFA appointments.
 *
 * Sections:
 *   1. Portfolio Overview  — key stats + allocation pie
 *   2. Advisor Performance — win rate, recent calls
 *   3. Questions to Ask    — generated from portfolio state
 *   4. Active Insights     — proactive insights engine output
 *   5. Goals Progress      — life-goals progress bars
 *   6. Actions Since Last Meeting — recent trades
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Briefcase, Printer, RefreshCw, TrendingUp, TrendingDown,
  CheckCircle, XCircle, Clock, Target, Lightbulb,
  HelpCircle, ChevronRight, FileText, Activity,
} from 'lucide-react'
import { format } from 'date-fns'
import { safeFormat } from '../lib/format'
import { Link } from 'react-router-dom'
import {
  api,
  getHouseholdSummary,
  getInsights,
  getLifeGoals,
  getExecutedTrades,
} from '@/lib/api'
import type {
  Insight,
  LifeGoal,
} from '@/lib/api'
import type {
  HouseholdSummary,
  ExecutedTrade,
  TypeBreakdown,
} from '@/lib/types'

// ── backend advisor contract (normalised here, not in lib/api) ───────────────
// Scorecards emit *_pct stats; recommendation rows carry created_at and
// status 'open'/'closed'/'expired' — win/loss derives from outcome_pct sign.

interface ScorecardRow {
  advisor_name: string
  total_recommendations: number
  win_rate_pct?: number | null
  avg_return_pct?: number | null
  best_call_pct?: number | null
  worst_call_pct?: number | null
}

interface AdvisorRecRow {
  id: number
  advisor_name: string
  recommendation?: string | null
  ticker?: string | null
  action?: string | null
  rationale?: string | null
  outcome_pct?: number | null
  status?: string | null
  created_at?: string | null
}

function recIsClosed(r: AdvisorRecRow): boolean {
  const s = (r.status ?? '').toLowerCase()
  return s === 'closed' || s === 'expired'
}

// ── helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

function fmtPct(n: number | null | undefined, dp = 1): string {
  if (n == null) return '—'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(dp)}%`
}

function progressColor(pct: number): string {
  if (pct >= 75) return '#22c55e'
  if (pct >= 50) return '#10b981'
  if (pct >= 25) return '#f59e0b'
  return '#f43f5e'
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="card p-6 space-y-4 print:shadow-none print:border print:border-gray-200">
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <span className="text-blue-light">{icon}</span>
        <h2 className="panel-title">{title}</h2>
      </div>
      {children}
    </div>
  )
}

// ── 1. Portfolio Overview ─────────────────────────────────────────────────────

function PortfolioOverview({ summary }: { summary: HouseholdSummary | null }) {
  if (!summary) {
    return (
      <div className="space-y-3 py-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-20 skeleton" />)}
        </div>
        <p className="text-center text-text-muted text-sm">Loading portfolio data…</p>
      </div>
    )
  }

  const totalValue = summary.total_net_worth ?? 0
  const invested   = summary.tracked_investments_gbp ?? 0
  const property   = summary.property_net_equity_gbp ?? 0
  const discPct    = summary.discretionary_pct ?? 0

  // Build allocation slices from by_type
  const TYPE_COLORS: Record<string, string> = {
    SIPP:        '#3b82f6',
    ISA:         '#10b981',
    GIA:         '#f59e0b',
    Crypto:      '#a855f7',
    Bank:        '#a1a1aa',
    'Real Estate': '#f97316',
    Property:    '#f97316',
    Liabilities: '#ef4444',
  }
  const FALLBACK = ['#3b82f6','#10b981','#f59e0b','#a855f7','#f97316','#ef4444','#06b6d4']

  interface Slice { label: string; value: number; color: string }

  const slices: Slice[] = (summary.by_type ?? [])
    .filter((t: TypeBreakdown) => t.total_value > 0)
    .sort((a: TypeBreakdown, b: TypeBreakdown) => b.total_value - a.total_value)
    .map((t: TypeBreakdown, i: number) => ({
      label: t.account_type,
      value: t.total_value,
      color: TYPE_COLORS[t.account_type] ?? FALLBACK[i % FALLBACK.length],
    }))

  const total = slices.reduce((s: number, x: Slice) => s + x.value, 0)

  // Mini SVG donut
  const R = 40
  const CX = 52
  const CY = 52
  const CIRC = 2 * Math.PI * R
  const GAP = 2
  let cum = 0
  const segments = slices.map((s: Slice) => {
    const frac = total > 0 ? s.value / total : 0
    const painted = Math.max(frac * CIRC - GAP, 0)
    const offset = CIRC - cum * CIRC
    cum += frac
    return { ...s, painted, offset }
  })

  return (
    <div className="space-y-4">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
        <div className="stat-card card-hover">
          <p className="stat-label">Total Net Worth</p>
          <p className="stat-hero text-gradient">{fmt(totalValue)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Invested Assets</p>
          <p className="stat-value text-2xl">{fmt(invested)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Property Equity</p>
          <p className="stat-value text-2xl">{fmt(property)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Discretionary</p>
          <p className="stat-value text-2xl">{discPct.toFixed(1)}%</p>
          <p className="text-xs text-text-muted">You can trade</p>
        </div>
      </div>

      {/* Allocation */}
      {slices.length > 0 && (
        <div className="flex items-center gap-6">
          <svg
            viewBox="0 0 104 104"
            className="w-24 h-24 shrink-0"
            role="img"
            aria-label="Allocation donut"
          >
            <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(148,163,184,0.12)" strokeWidth={12} />
            {segments.map((seg, i) => (
              <circle
                key={i}
                cx={CX} cy={CY} r={R}
                fill="none"
                stroke={seg.color}
                strokeWidth={12}
                strokeDasharray={`${seg.painted} ${CIRC}`}
                strokeDashoffset={seg.offset}
                transform={`rotate(-90 ${CX} ${CY})`}
              >
                <title>{seg.label}: {fmt(seg.value)}</title>
              </circle>
            ))}
          </svg>
          <ul className="flex-1 space-y-1.5">
            {slices.map((s: Slice) => {
              const pct = total > 0 ? (s.value / total) * 100 : 0
              return (
                <li key={s.label} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="flex-1 text-text-secondary">{s.label}</span>
                  <span className="font-mono text-text-muted">{pct.toFixed(1)}%</span>
                  <span className="font-mono text-text-primary w-24 text-right">
                    {s.value >= 1e6 ? `£${(s.value/1e6).toFixed(2)}M` : fmt(s.value)}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── 2. Advisor Performance ────────────────────────────────────────────────────

function AdvisorPerformance({
  scorecards,
  recommendations,
}: {
  scorecards: ScorecardRow[]
  recommendations: AdvisorRecRow[]
}) {
  if (scorecards.length === 0) {
    return (
      <p className="text-sm text-text-muted py-4 text-center">
        No advisor data yet — log recommendations on the{' '}
        <Link to="/app/advisor" className="text-blue-light hover:underline">Advisor Scorecard</Link> page.
      </p>
    )
  }

  // Show last 5 recommendations (most recent first)
  const recent = [...recommendations]
    .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime())
    .slice(0, 5)

  return (
    <div className="space-y-4">
      {/* Scorecards */}
      <div className="grid md:grid-cols-2 gap-3">
        {scorecards.map(sc => (
          <div
            key={sc.advisor_name}
            className="rounded-lg border border-border bg-surface/40 p-4 space-y-2"
          >
            <div className="flex items-center justify-between">
              <p className="font-semibold text-text-primary text-sm">{sc.advisor_name}</p>
              <span
                className={`text-xs font-bold px-2 py-0.5 rounded font-mono ${
                  (sc.win_rate_pct ?? 0) >= 60
                    ? 'bg-emerald-900/40 text-emerald-300'
                    : (sc.win_rate_pct ?? 0) >= 45
                    ? 'bg-amber-900/40 text-amber-300'
                    : 'bg-rose-900/40 text-rose-300'
                }`}
              >
                {(sc.win_rate_pct ?? 0).toFixed(0)}% win rate
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <p className="text-text-muted">Total calls</p>
                <p className="font-mono text-text-primary">{sc.total_recommendations}</p>
              </div>
              <div>
                <p className="text-text-muted">Avg return</p>
                <p className={`font-mono ${(sc.avg_return_pct ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-light'}`}>
                  {fmtPct(sc.avg_return_pct)}
                </p>
              </div>
              <div>
                <p className="text-text-muted">Best call</p>
                <p className="font-mono text-emerald-400">{fmtPct(sc.best_call_pct)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent calls */}
      {recent.length > 0 && (
        <div>
          <p className="panel-title mb-2">
            Recent Recommendations
          </p>
          <ul className="space-y-1.5">
            {recent.map(r => (
              <li
                key={r.id}
                className="flex items-center gap-3 text-xs py-1.5 border-b border-border last:border-0"
              >
                {r.status === 'WIN'  && <CheckCircle size={13} className="text-emerald-400 shrink-0" />}
                {r.status === 'LOSS' && <XCircle     size={13} className="text-rose-light shrink-0" />}
                {r.status === 'OPEN' && <Clock       size={13} className="text-amber-400 shrink-0" />}
                <span className="font-mono font-bold text-text-primary w-16 shrink-0">{r.ticker}</span>
                <span className={`badge text-[10px] shrink-0 ${
                  r.action === 'BUY' || r.action === 'ADD'
                    ? 'badge-green'
                    : r.action === 'SELL' || r.action === 'REDUCE'
                    ? 'badge-red'
                    : 'badge-amber'
                }`}>{r.action}</span>
                <span className="flex-1 text-text-muted truncate">{r.rationale ?? r.advisor_name}</span>
                {r.outcome_pct != null && (
                  <span className={`font-mono shrink-0 ${r.outcome_pct >= 0 ? 'text-emerald-400' : 'text-rose-light'}`}>
                    {fmtPct(r.outcome_pct)}
                  </span>
                )}
                <span className="text-text-muted shrink-0 font-mono">
                  {safeFormat(r.created_at, 'd MMM yy', format)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── 3. Questions to Ask ───────────────────────────────────────────────────────

interface Question {
  id: string
  text: string
  reason: string
  priority: 'high' | 'medium' | 'low'
}

function buildQuestions(
  summary: HouseholdSummary | null,
  insights: Insight[],
): Question[] {
  const questions: Question[] = []

  if (!summary) return questions

  const totalValue = summary.total_net_worth ?? 0

  // Cash drag check
  const cashEntry = (summary.by_type ?? []).find(
    t => t.account_type === 'Bank' || t.account_type === 'Cash',
  )
  const cashValue = cashEntry?.total_value ?? 0
  const cashPct   = totalValue > 0 ? (cashValue / totalValue) * 100 : 0
  if (cashPct > 10) {
    questions.push({
      id: 'cash-drag',
      text: `Why is ${fmt(cashValue)} sitting in cash? Should we put it to work?`,
      reason: `Cash represents ${cashPct.toFixed(1)}% of the portfolio — above the 10% drag threshold.`,
      priority: 'high',
    })
  }

  // ISA funding check
  const isaEntry = (summary.by_type ?? []).find(t => t.account_type === 'ISA')
  const isaValue = isaEntry?.total_value ?? 0
  const ISA_ANNUAL_ALLOWANCE = 20_000
  // If ISA value seems relatively low compared to portfolio, suggest funding
  if (isaValue < ISA_ANNUAL_ALLOWANCE * 2 && totalValue > 100_000) {
    questions.push({
      id: 'isa-funding',
      text: 'Can we prioritise ISA funding this tax year to maximise the £20k allowance?',
      reason: `Current ISA balance is ${fmt(isaValue)} — there may be room to use this tax year's allowance.`,
      priority: 'medium',
    })
  }

  // Concentration check — if any single account type is > 60% of portfolio
  const largeSlice = (summary.by_type ?? []).find(
    t => totalValue > 0 && (t.total_value / totalValue) * 100 > 60 && t.account_type !== 'SIPP',
  )
  if (largeSlice) {
    const pct = ((largeSlice.total_value / totalValue) * 100).toFixed(1)
    questions.push({
      id: 'concentration',
      text: `${largeSlice.account_type} is ${pct}% of our portfolio — should we rebalance toward target allocation?`,
      reason: `Heavy concentration in a single wrapper or asset type increases risk.`,
      priority: 'medium',
    })
  }

  // Rebalancing need — discretionary vs non-discretionary imbalance
  if (summary.discretionary_pct > 70) {
    questions.push({
      id: 'rebalance',
      text: 'Should we rebalance toward target allocation given the high discretionary exposure?',
      reason: `${summary.discretionary_pct.toFixed(1)}% of assets are in discretionary accounts — higher than typical.`,
      priority: 'low',
    })
  }

  // Mortgage / property question — always include if property equity exists
  if ((summary.property_net_equity_gbp ?? 0) > 0) {
    questions.push({
      id: 'mortgage',
      text: 'What is the current remortgage plan for our properties? Are any fixed rates expiring soon?',
      reason: 'Property equity is a material part of net worth — rate exposure should be reviewed regularly.',
      priority: 'medium',
    })
  }

  // Insight-driven questions
  const highInsights = insights.filter(i => i.priority === 'high').slice(0, 2)
  highInsights.forEach(ins => {
    questions.push({
      id: `insight-${ins.id}`,
      text: `Insight flagged: "${ins.title}" — how do you recommend we act on this?`,
      reason: ins.message,
      priority: 'high',
    })
  })

  return questions
}

function QuestionsToAsk({
  summary,
  insights,
}: {
  summary: HouseholdSummary | null
  insights: Insight[]
}) {
  const questions = buildQuestions(summary, insights)

  const PRIORITY_COLOR: Record<string, string> = {
    high:   'border-l-rose-500 bg-rose-900/10',
    medium: 'border-l-amber-500 bg-amber-900/10',
    low:    'border-l-blue-500 bg-blue-900/10',
  }
  const PRIORITY_LABEL: Record<string, string> = {
    high:   'text-rose-400',
    medium: 'text-amber-400',
    low:    'text-blue-400',
  }

  if (questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
        <CheckCircle size={22} className="text-emerald-400 opacity-60" />
        <p className="text-sm text-text-muted">Portfolio looks healthy — no urgent questions flagged.</p>
      </div>
    )
  }

  return (
    <ul className="space-y-3" aria-label="Questions to raise with advisor">
      {questions.map(q => (
        <li
          key={q.id}
          className={`rounded-lg border-l-4 px-4 py-3 ${PRIORITY_COLOR[q.priority] ?? ''}`}
        >
          <p className="text-sm font-medium text-text-primary leading-snug">{q.text}</p>
          <p className="text-xs text-text-muted mt-1 leading-relaxed">{q.reason}</p>
          <span className={`text-[10px] font-semibold uppercase tracking-wider mt-1 inline-block ${PRIORITY_LABEL[q.priority]}`}>
            {q.priority} priority
          </span>
        </li>
      ))}
    </ul>
  )
}

// ── 4. Active Insights ────────────────────────────────────────────────────────

const PRIORITY_ICON: Record<string, string> = {
  high:   '🔴',
  medium: '🟡',
  low:    '🟢',
}

const CATEGORY_BADGE: Record<string, string> = {
  market:      'bg-blue-900/40 text-blue-300',
  tax:         'bg-purple-900/40 text-purple-300',
  risk:        'bg-rose-900/40 text-rose-300',
  opportunity: 'bg-emerald-900/40 text-emerald-300',
  action:      'bg-amber-900/40 text-amber-300',
  info:        'bg-surface-hover text-text-secondary',
}

function ActiveInsights({
  insights,
  loading,
}: {
  insights: Insight[]
  loading: boolean
}) {
  if (loading) {
    return (
      <ul className="space-y-2" aria-busy>
        {[...Array(3)].map((_, i) => (
          <li key={i} className="h-12 skeleton" />
        ))}
      </ul>
    )
  }

  if (insights.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
        <Lightbulb size={22} className="text-text-muted opacity-30" />
        <p className="text-sm text-text-muted">No active insights — portfolio looks calm.</p>
      </div>
    )
  }

  return (
    <ul className="space-y-2.5" aria-label="Active insights">
      {insights.map(ins => (
        <li
          key={ins.id}
          className="flex items-start gap-3 rounded-lg border border-border bg-surface/40 px-3 py-2.5"
        >
          <span className="text-base mt-0.5 shrink-0">{PRIORITY_ICON[ins.priority] ?? '⚪'}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-text-primary">{ins.title}</p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${CATEGORY_BADGE[ins.category] ?? CATEGORY_BADGE.info}`}>
                {ins.category}
              </span>
            </div>
            <p className="text-xs text-text-muted mt-0.5 leading-relaxed">{ins.message}</p>
          </div>
        </li>
      ))}
    </ul>
  )
}

// ── 5. Goals Progress ─────────────────────────────────────────────────────────

function GoalsProgress({ goals }: { goals: LifeGoal[] }) {
  const active = goals.filter(g => g.status === 'ACTIVE').slice(0, 6)

  if (active.length === 0) {
    return (
      <p className="text-sm text-text-muted py-4 text-center">
        No active goals — add them on the{' '}
        <Link to="/app/life-goals" className="text-blue-light hover:underline">Life Goals</Link> page.
      </p>
    )
  }

  return (
    <ul className="space-y-4" aria-label="Goals progress">
      {active.map(goal => {
        const pct = goal.target_amount > 0
          ? Math.min((goal.current_saved / goal.target_amount) * 100, 100)
          : 0
        const color = progressColor(pct)
        return (
          <li key={goal.id} className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-text-primary">{goal.name}</span>
              <span className="text-xs text-text-muted font-mono">
                {fmt(goal.current_saved)} / {fmt(goal.target_amount)}
              </span>
            </div>
            <div className="h-2 bg-surface-hover rounded-full overflow-hidden" role="presentation">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-text-muted">
              <span>{pct.toFixed(0)}% saved</span>
              <span>
                {goal.target_date
                  ? safeFormat(goal.target_date, 'd MMM yyyy', format)
                  : 'No target date'}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

// ── 6. Actions Since Last Meeting ─────────────────────────────────────────────

function ActionsSinceLastMeeting({ trades }: { trades: ExecutedTrade[] }) {
  // Show last 10 trades, most recent first
  const recent = [...trades]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 10)

  if (recent.length === 0) {
    return (
      <p className="text-sm text-text-muted py-4 text-center">
        No recent trades recorded.
      </p>
    )
  }

  return (
    <ul className="space-y-2" aria-label="Recent trades">
      {recent.map(t => (
        <li
          key={t.id}
          className="flex items-center gap-3 text-xs py-1.5 border-b border-border last:border-0"
        >
          <Activity size={12} className="text-text-muted shrink-0" />
          <span className={`badge text-[10px] shrink-0 ${
            t.action === 'BUY' ? 'badge-green' : 'badge-red'
          }`}>{t.action}</span>
          <span className="font-mono font-bold text-text-primary w-16 shrink-0">{t.ticker}</span>
          <span className="flex-1 text-text-muted truncate">
            {t.status === 'OPEN' ? 'Open position' : t.status}
            {t.paper ? ' (paper)' : ''}
          </span>
          <span className="font-mono text-text-muted">
            @ £{t.entry_price.toFixed(2)}
          </span>
          <span className="font-mono text-text-primary shrink-0">{fmt(t.size_gbp)}</span>
          {t.pnl_pct != null && (
            <span className={`font-mono shrink-0 ${t.pnl_pct >= 0 ? 'text-emerald-400' : 'text-rose-light'}`}>
              {fmtPct(t.pnl_pct)}
            </span>
          )}
          <span className="text-text-muted shrink-0 font-mono">
            {safeFormat(t.created_at, 'd MMM yy', format)}
          </span>
        </li>
      ))}
    </ul>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function IfaMeetingPage() {
  const [summary,     setSummary]     = useState<HouseholdSummary | null>(null)
  const [scorecards,  setScorecards]  = useState<ScorecardRow[]>([])
  const [recs,        setRecs]        = useState<AdvisorRecRow[]>([])
  const [insights,    setInsights]    = useState<Insight[]>([])
  const [goals,       setGoals]       = useState<LifeGoal[]>([])
  const [trades,      setTrades]      = useState<ExecutedTrade[]>([])

  const [loading,     setLoading]     = useState(true)
  const [insightsLoading, setInsightsLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [sum, sc, rec, gl, tr] = await Promise.allSettled([
        getHouseholdSummary(),
        api.get<ScorecardRow[]>('/api/advisor/scorecard').then(r => r.data ?? []),
        api.get<AdvisorRecRow[]>('/api/advisor/recommendations').then(r => r.data ?? []),
        getLifeGoals(),
        getExecutedTrades(),
      ])
      if (sum.status === 'fulfilled')  setSummary(sum.value)
      if (sc.status === 'fulfilled')   setScorecards(sc.value)
      if (rec.status === 'fulfilled')  setRecs(rec.value)
      if (gl.status === 'fulfilled')   setGoals(gl.value)
      if (tr.status === 'fulfilled')   setTrades(tr.value)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    // Load insights separately — they can be slow
    getInsights()
      .then(r => setInsights(r.insights ?? []))
      .catch(() => {})
      .finally(() => setInsightsLoading(false))
  }, [load]) // eslint-disable-line

  return (
    <div className="space-y-6 animate-fade-in print:space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue/20 border border-blue/30 flex items-center justify-center">
            <Briefcase size={20} className="text-blue-light" />
          </div>
          <div>
            <h1 className="page-title">IFA Meeting Prep</h1>
            <p className="text-sm text-text-secondary">
              {format(new Date(), 'EEEE, d MMMM yyyy')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 print:hidden">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="btn-ghost p-1.5 rounded-lg"
            aria-label="Refresh data"
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin text-blue-light' : ''} />
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="btn-secondary text-xs gap-1.5"
            aria-label="Print report"
          >
            <Printer size={13} />
            Print Report
          </button>
          <a
            href="/api/reports/ifa-pack"
            target="_blank"
            rel="noreferrer"
            className="btn-primary text-xs gap-1.5"
          >
            <FileText size={13} />
            Generate Full Report
          </a>
        </div>
      </div>

      {/* 1. Portfolio Overview */}
      <Section title="Portfolio Overview" icon={<TrendingUp size={16} />}>
        <PortfolioOverview summary={summary} />
      </Section>

      {/* 2. Advisor Performance */}
      <Section title="Advisor Performance" icon={<CheckCircle size={16} />}>
        <AdvisorPerformance scorecards={scorecards} recommendations={recs} />
      </Section>

      {/* 3. Questions to Ask */}
      <Section title="Questions to Ask" icon={<HelpCircle size={16} />}>
        <p className="text-xs text-text-muted -mt-2 mb-2">
          Auto-generated from your portfolio state. Bring these to your meeting.
        </p>
        <QuestionsToAsk summary={summary} insights={insights} />
      </Section>

      {/* 4. Active Insights */}
      <Section title="Active Insights" icon={<Lightbulb size={16} />}>
        <div className="flex items-center justify-between -mt-2 mb-2">
          <p className="text-xs text-text-muted">Proactive insights from the analysis engine.</p>
          <Link
            to="/app"
            className="text-xs text-blue-light hover:underline flex items-center gap-1 print:hidden"
          >
            Dashboard <ChevronRight size={11} />
          </Link>
        </div>
        <ActiveInsights insights={insights} loading={insightsLoading} />
      </Section>

      {/* 5. Goals Progress */}
      <Section title="Goals Progress" icon={<Target size={16} />}>
        <div className="flex items-center justify-between -mt-2 mb-2">
          <p className="text-xs text-text-muted">Progress toward your life-goal milestones.</p>
          <Link
            to="/app/life-goals"
            className="text-xs text-blue-light hover:underline flex items-center gap-1 print:hidden"
          >
            All goals <ChevronRight size={11} />
          </Link>
        </div>
        <GoalsProgress goals={goals} />
      </Section>

      {/* 6. Actions Since Last Meeting */}
      <Section title="Actions Since Last Meeting" icon={<Activity size={16} />}>
        <div className="flex items-center justify-between -mt-2 mb-2">
          <p className="text-xs text-text-muted">Recent trades and portfolio activity.</p>
          <Link
            to="/app/trades"
            className="text-xs text-blue-light hover:underline flex items-center gap-1 print:hidden"
          >
            Trade log <ChevronRight size={11} />
          </Link>
        </div>
        <ActionsSinceLastMeeting trades={trades} />
      </Section>

      {/* Footer CTA */}
      <div className="flex items-center justify-end gap-3 print:hidden">
        <a
          href="/api/reports/ifa-pack"
          target="_blank"
          rel="noreferrer"
          className="btn-primary text-sm gap-2"
        >
          <FileText size={14} />
          Generate Full IFA Pack
        </a>
      </div>
    </div>
  )
}
